import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCopy, FiSettings, FiPlus, FiTrash2, FiRefreshCw, FiDownload, FiAlertTriangle, FiPlay } from 'react-icons/fi';
import './WordlistGeneratorDemo.css';

// Simple combinator for wordlists with sane limits and options
const generateWordlist = ({
  baseWords, // array of strings
  separators = [''], // e.g. ['', '-', '_', '.']
  leet = false,
  years = [], // e.g. [1998, 2000]
  suffixes = [], // e.g. ['!', '@', '123']
  maxCombinations = 10000 // limit to avoid UI freeze
}) => {
  const uniq = new Set();
  const push = (s) => { if (s && !uniq.has(s)) uniq.add(s); };
  const words = baseWords
    .map(w => (w || '').trim())
    .filter(Boolean);
  if (words.length === 0) return [];

  // base variants (case, leet + extras: reversed, doubled)
  const variants = (w) => {
    const cap = w[0] ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w;
    const base = new Set([w, w.toLowerCase(), w.toUpperCase(), cap]);

    // reversed and doubled
    const rev = w.split('').reverse().join('');
    base.add(rev);
    base.add(w + w);

    if (leet) {
      const leetMap = { a: '4', e: '3', i: '1', o: '0', s: '5', t: '7' };
      const toLeet = (str) => str.replace(/[aeiost]/gi, ch => {
        const isUpper = ch === ch.toUpperCase();
        const rep = leetMap[ch.toLowerCase()] || ch;
        return isUpper ? String(rep).toUpperCase() : rep;
      });
      [...base].forEach(v => base.add(toLeet(v)));
    }
    return [...base];
  };

  // Start building combinations
  const result = [];

  // 1) single words + years/suffixes
  for (const w of words) {
    for (const v of variants(w)) {
      push(v);
      for (const y of years) push(`${v}${y}`);
      for (const s of suffixes) push(`${v}${s}`);
    }
  }

  // 2) pair combinations with separators
  for (let i = 0; i < words.length; i++) {
    for (let j = 0; j < words.length; j++) {
      if (i === j) continue;
      for (const a of variants(words[i])) {
        for (const b of variants(words[j])) {
          for (const sep of separators) {
            push(`${a}${sep}${b}`);
            for (const y of years) push(`${a}${sep}${b}${y}`);
            for (const s of suffixes) push(`${a}${sep}${b}${s}`);
          }
        }
      }
    }
  }

  // 3) triple combinations (a sep b sep c)
  for (let i = 0; i < words.length; i++) {
    for (let j = 0; j < words.length; j++) {
      for (let k = 0; k < words.length; k++) {
        if (i === j || j === k || i === k) continue;
        for (const a of variants(words[i])) {
          for (const b of variants(words[j])) {
            for (const c of variants(words[k])) {
              for (const sep of separators) {
                push(`${a}${sep}${b}${sep}${c}`);
                for (const y of years) push(`${a}${sep}${b}${sep}${c}${y}`);
                for (const s of suffixes) push(`${a}${sep}${b}${sep}${c}${s}`);
              }
            }
          }
        }
      }
    }
  }

  // 3) cap the size and return as array
  for (const item of uniq) {
    result.push(item);
    if (result.length >= maxCombinations) break;
  }
  return result;
};

const MAX_NAMES = 10;
const MAX_YEARS = 6;
const MAX_SUFFIXES = 6;

const WordlistGeneratorDemo = () => {
  const [inputs, setInputs] = useState(['nome', 'sobrenome']);
  const [years, setYears] = useState(['1998', '2000']);
  const [suffixes, setSuffixes] = useState(['!', '123']);
  const [useLeet, setUseLeet] = useState(true);
  const [separators, setSeparators] = useState(['', '-', '_']);
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [maxComb, setMaxComb] = useState(10000);
  const [warnTruncated, setWarnTruncated] = useState(false);

  const [wordlist, setWordlist] = useState([]);
  const canGenerate = maxComb <= 10000; // block when above 10k

  const handleGenerate = () => {
    if (!canGenerate) return; // safety
    const parsedYears = years
      .map(y => parseInt(String(y).trim(), 10))
      .filter(n => !isNaN(n));

    const list = generateWordlist({
      baseWords: inputs,
      separators,
      leet: useLeet,
      years: parsedYears,
      suffixes,
      maxCombinations: Math.max(100, Math.min(20000, maxComb))
    });
    setWarnTruncated(list.length >= Math.max(100, Math.min(20000, maxComb)));
    setWordlist(list);
  };

  const addInput = () => setInputs(prev => (prev.length < MAX_NAMES ? [...prev, ''] : prev));
  const removeInput = (idx) => setInputs(prev => prev.filter((_, i) => i !== idx));
  const updateInput = (idx, value) => setInputs(prev => prev.map((v, i) => i === idx ? value : v));

  const addYear = () => setYears(prev => (prev.length < MAX_YEARS ? [...prev, ''] : prev));
  const removeYear = (idx) => setYears(prev => prev.filter((_, i) => i !== idx));
  const updateYear = (idx, value) => setYears(prev => prev.map((v, i) => i === idx ? value : v));

  const addSuffix = () => setSuffixes(prev => (prev.length < MAX_SUFFIXES ? [...prev, ''] : prev));
  const removeSuffix = (idx) => setSuffixes(prev => prev.filter((_, i) => i !== idx));
  const updateSuffix = (idx, value) => setSuffixes(prev => prev.map((v, i) => i === idx ? value : v));

  const toggleSeparator = (sep) => {
    setSeparators(prev => prev.includes(sep) ? prev.filter(s => s !== sep) : [...prev, sep]);
  };

  const copyAll = async () => {
    if (!wordlist.length) return;
    try {
      await navigator.clipboard.writeText(wordlist.join('\n'));
      setCopying(true);
      setTimeout(() => setCopying(false), 1500);
    } catch (e) {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = wordlist.join('\n');
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopying(true);
      setTimeout(() => setCopying(false), 1500);
    }
  };

  const resetForm = () => {
    setInputs(['nome', 'sobrenome']);
    setYears(['1998', '2000']);
    setSuffixes(['!', '123']);
    setUseLeet(true);
    setSeparators(['', '-', '_']);
  };

  const downloadTxt = () => {
    if (!wordlist.length) return;
    setDownloading(true);
    const blob = new Blob([wordlist.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wordlist.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTimeout(() => setDownloading(false), 600);
  };

  // Reset should not auto-generate
  React.useEffect(() => {
    setWordlist([]);
    setWarnTruncated(false);
  }, [inputs.length, years.length, suffixes.length, useLeet, separators.join(','), maxComb]);

  return (
    <div className="wordlist-generator-demo">
      <div className="wg-header">
        <FiSettings className="wg-icon" />
        <h3>Wordlist Generator</h3>
      </div>
      <p className="wg-description">Gere wordlists personalizadas combinando nomes, datas, sufixos e variações.</p>

      <div className="wg-grid">
        {/* Inputs principais */}
        <div className="wg-card">
          <h4>Informações base</h4>
          {inputs.map((val, idx) => (
            <div className="wg-row" key={idx}>
              <input type="text" value={val} placeholder="Ex.: nome, apelido, empresa"
                     onChange={(e) => updateInput(idx, e.target.value)} />
              <button className="wg-btn" onClick={() => removeInput(idx)} title="Remover"><FiTrash2 /></button>
            </div>
          ))}
          <div className="wg-actions">
            <button className="wg-btn" onClick={addInput} disabled={inputs.length >= MAX_NAMES}
                    title={inputs.length >= MAX_NAMES ? `Máximo de ${MAX_NAMES} nomes` : 'Adicionar campo'}>
              <FiPlus /> Adicionar campo
            </button>
          </div>
          <div className="wg-badges">
            <span className="wg-badge">Variações: minúsculas, MAIÚSCULAS, Capitalizada</span>
            <span className="wg-badge">Leet opcional</span>
          </div>
        </div>

        {/* Datas e sufixos */}
        <div className="wg-card">
          <h4>Datas e sufixos</h4>
          <div>
            <strong>Anos</strong>
            {years.map((val, idx) => (
              <div className="wg-row" key={idx}>
                <input type="number" value={val} placeholder="Ex.: 1998"
                       onChange={(e) => updateYear(idx, e.target.value)} />
                <button className="wg-btn" onClick={() => removeYear(idx)} title="Remover"><FiTrash2 /></button>
              </div>
            ))}
            <div className="wg-actions">
              <button className="wg-btn" onClick={addYear} disabled={years.length >= MAX_YEARS}
                      title={years.length >= MAX_YEARS ? `Máximo de ${MAX_YEARS} datas` : 'Adicionar ano'}>
                <FiPlus /> Adicionar ano
              </button>
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            <strong>Sufixos</strong>
            {suffixes.map((val, idx) => (
              <div className="wg-row" key={idx}>
                <input type="text" value={val} placeholder="Ex.: !, 123, @"
                       onChange={(e) => updateSuffix(idx, e.target.value)} />
                <button className="wg-btn" onClick={() => removeSuffix(idx)} title="Remover"><FiTrash2 /></button>
              </div>
            ))}
            <div className="wg-actions">
              <button className="wg-btn" onClick={addSuffix} disabled={suffixes.length >= MAX_SUFFIXES}
                      title={suffixes.length >= MAX_SUFFIXES ? `Máximo de ${MAX_SUFFIXES} sufixos` : 'Adicionar sufixo'}>
                <FiPlus /> Adicionar sufixo
              </button>
            </div>
          </div>
        </div>

        {/* Opções */}
        <div className="wg-card">
          <h4>Opções</h4>
          <div className="wg-badges">
            {['', '-', '_', '.'].map(sep => (
              <button key={sep || 'none'} className="wg-btn" onClick={() => toggleSeparator(sep)}>
                {separators.includes(sep) ? '✓' : '×'} Separador "{sep || 'nada'}"
              </button>
            ))}
          </div>
          <div className="wg-actions">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={useLeet} onChange={(e) => setUseLeet(e.target.checked)} />
              Aplicar variações leet (a=4, e=3, i=1, o=0...)
            </label>
          </div>
          <div className="wg-actions" style={{ gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label className="setting-label">Limite (linhas):</label>
              <input type="number" min="100" max="20000" value={maxComb}
                     onChange={(e) => setMaxComb(Number(e.target.value) || 100)}
                     className="length-input" style={{ width: 120 }} />
            </div>
            <button className="wg-btn" onClick={resetForm}><FiRefreshCw /> Resetar</button>
            <motion.button className="wg-btn primary" onClick={handleGenerate} disabled={!canGenerate}
              title={canGenerate ? 'Gerar a lista' : 'Limite acima de 10000 não permitido'}
              whileHover={{ scale: canGenerate ? 1.02 : 1 }} whileTap={{ scale: canGenerate ? 0.98 : 1 }}>
              <FiPlay /> Gerar lista
            </motion.button>
          </div>
        </div>

        {/* Resultado */}
        <div className="wg-card">
          <h4>Resultado</h4>
          {!canGenerate && (
            <div className="wg-warning">
              <FiAlertTriangle /> O limite está acima de 10.000. Reduza para gerar a lista.
            </div>
          )}
          <div className="wg-output">
            {wordlist.map((line, idx) => (
              <div className="line" key={idx}>{line}</div>
            ))}
            {!wordlist.length && <div className="line">Adicione informações e clique em "Gerar lista"…</div>}
          </div>
          {warnTruncated && (
            <div className="wg-warning">
              <FiAlertTriangle /> Resultado truncado pelo limite configurado.
            </div>
          )}
          <div className="wg-footer">
            <div className="wg-counter">{wordlist.length} linhas</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <motion.button className="wg-btn" onClick={downloadTxt} disabled={!wordlist.length || downloading}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <FiDownload /> {downloading ? 'Baixando...' : 'Baixar .txt'}
              </motion.button>
              <motion.button className="wg-btn" onClick={copyAll} disabled={!wordlist.length}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <FiCopy /> Copiar tudo
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Notificação */}
      <AnimatePresence>
        {copying && (
          <motion.div className="copy-notification"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
            Wordlist copiada!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WordlistGeneratorDemo;